import { useEffect, useState } from "react";
import { SHIFT_PRODUCTION_FORM_API_BASE, shiftFormDailyCoverageResponseSchema, type ShiftProductionDepartmentName } from "../../../shared/shift-production-form/contracts";
import { apiRequest } from "../../shared/api/client";
import { shiftAnalysisDateRange } from "./analysis-date-range";
import { analysisCoverageComparison } from "./analysis-coverage-comparison";

type Coverage = ReturnType<typeof shiftFormDailyCoverageResponseSchema.parse>;
type Result = { scope: string; current: Coverage | null; previous: Coverage | null; phase: "loading" | "ready" | "error"; error?: string };

export function useAnalysisDailyCoverage(dateFrom?: string, dateTo?: string, departmentName?: ShiftProductionDepartmentName, testMode = false) {
  const [version, setVersion] = useState(0);
  const singleDay = !testMode && Boolean(dateFrom && dateFrom === dateTo);
  const scope = JSON.stringify([dateFrom, dateTo, departmentName, version]);
  const [result, setResult] = useState<Result | null>(null);
  useEffect(() => {
    if (!singleDay || !dateFrom) return;
    let active = true;
    const controller = new AbortController();
    async function load(date: string) {
      const query = new URLSearchParams({ productionDate: date });
      if (departmentName) query.set("departmentName", departmentName);
      const value = await apiRequest<unknown>(`${SHIFT_PRODUCTION_FORM_API_BASE}/manager/daily-coverage?${query}`, { cache: "no-store", signal: controller.signal });
      return shiftFormDailyCoverageResponseSchema.parse(value);
    }
    const prior = shiftAnalysisDateRange({ dateFrom, dateTo: dateFrom }, -1)?.dateFrom;
    setResult({ scope, current: null, previous: null, phase: "loading" });
    void Promise.all([load(dateFrom), prior ? load(prior).catch(() => null) : Promise.resolve(null)]).then(([current, previous]) => {
      if (active) setResult({ scope, current, previous, phase: "ready" });
    }).catch(reason => {
      if (active) setResult({ scope, current: null, previous: null, phase: "error", error: reason instanceof Error ? reason.message : "Günlük kapsam alınamadı." });
    });
    return () => { active = false; controller.abort(); };
  }, [dateFrom, departmentName, scope, singleDay]);
  const state = result?.scope === scope ? result : null;
  const comparisonAvailability = analysisCoverageComparison(singleDay, state?.current?.summary ?? null, state?.previous?.summary ?? null);
  return { current: state?.current ?? null, phase: state?.phase ?? (singleDay ? "loading" as const : "range" as const), error: state?.error, comparisonAvailability, reload: () => setVersion(value => value + 1) };
}
